import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, catchError, take, switchMap } from 'rxjs/operators';
import { selectSelectedMultiAssessment } from 'src/state/multi-assessment/multi-assessment.selectors';
import type { AssessmentConfig } from 'src/shared/utils/assessment-navigator';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { classRoomService } from 'src/shared/services/api/class-room.service';
import { LessonPlanService } from 'src/shared/services/api/lesson-plan/lesson-plan.service';
import { ParentsService } from 'src/shared/services/api/parents.service';
import type {
  AssistantAction,
  CommandContext,
  LocalCommandResult,
  UserRoleHint,
} from '../voice/types/voice-assistant.types';
import {
  ASSISTANT_NAVIGATION_ENABLED,
  isAssistantNavigationCommandId,
} from '../voice/assistant-feature-flags';

export function normalizeVoiceInput(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9\s'-]/gi, '')
    .trim();
}

interface LocalCommand {
  id: string;
  patterns: RegExp[];
  roles?: UserRoleHint[];
  run: (ctx: CommandContext, deps: RegistryDeps) => Observable<LocalCommandResult | null>;
}

type RegistryDeps = {
  router: Router;
  classRoom: classRoomService;
  lessonPlan: LessonPlanService;
  parents: ParentsService;
  store: Store;
};

@Injectable({
  providedIn: 'root',
})
export class VoiceCommandRegistryService {
  private router = inject(Router);
  private classRoom = inject(classRoomService);
  private lessonPlan = inject(LessonPlanService);
  private parents = inject(ParentsService);
  private store = inject(Store);

  private readonly commands: LocalCommand[] = [
    {
      id: 'nav_dashboard',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?dashboard\b/,
        /\bhome\s+screen\b/,
        /\bmain\s+dashboard\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening your dashboard.',
          action: navAction('/dashboard'),
        }),
    },
    {
      id: 'nav_classroom',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?class\s*room\b/,
        /\bmy\s+classroom\b/,
      ],
      run: ctx => {
        if (!ctx.classId || !ctx.teacherName) {
          return of({
            replySpoken:
              'I need your class to be set. Open the dashboard and select a class first.',
            action: null,
          });
        }
        return of({
          replySpoken: 'Opening your classroom.',
          action: navAction('/class-room', {
            classId: ctx.classId,
            teacherName: ctx.teacherName,
          }),
        });
      },
    },
    {
      id: 'nav_lesson_plan',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?lesson\s*plan\b/,
        /\bteaching\s+plan\b/,
      ],
      run: (_ctx, deps) =>
        deps.store.select(selectSelectedMultiAssessment).pipe(
          take(1),
          switchMap((assessment: AssessmentConfig | null) => {
            const sectionId = Number(assessment?.section_id) || 0;
            const classLevelId = Number(assessment?.class_level_id) || 0;
            const hasPlan =
              assessment?.has_lesson_plan === true ||
              assessment?.has_lesson_plan === 1;
            if (!classLevelId || !hasPlan) {
              return of({
                replySpoken:
                  'No lesson plan is configured for this class. Open a program that has a lesson plan.',
                action: null,
              });
            }
            return of({
              replySpoken: 'Opening lesson plans.',
              action: navAction('/lesson-plan', {
                classId: classLevelId,
                sectionId,
                classLevelId,
              }),
            });
          })
        ),
    },
    {
      id: 'nav_virtual_assistant',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?(virtual\s+)?assistant\b/,
        /\bteacher\s+assist\b/,
        /\bcontent\s+generator\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening the virtual assistant.',
          action: navAction('/vitual-assistant'),
        }),
    },
    {
      id: 'nav_profile',
      patterns: [
        /\b(open|go to|show)\s+(my\s+)?profile\b/,
        /\bteacher\s+profile\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening your profile.',
          action: navAction('/teacherprofile'),
        }),
    },
    {
      id: 'nav_connect_parents',
      patterns: [
        /\b(open|go to)\s+(connect\s+with\s+parents|parents)\b/,
        /\bparent\s+connect\b/,
      ],
      run: ctx => {
        if (ctx.teacherId) {
          return of({
            replySpoken: 'Opening parent connect.',
            action: navAction('/connect-with-parents-v2', {
              teacherId: ctx.teacherId,
            }),
          });
        }
        if (!ctx.classId) {
          return of({
            replySpoken: 'Open the dashboard to select a class for parent connect.',
            action: null,
          });
        }
        return of({
          replySpoken: 'Opening connect with parents.',
          action: navAction('/connect-with-parents', { classId: ctx.classId }),
        });
      },
    },
    {
      id: 'nav_assessments',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?(multiple\s+)?assessments?\b/,
        /\bprograms?\b/,
      ],
      run: ctx => {
        if (!ctx.schoolId) {
          return of({
            replySpoken: 'School information is missing. Try from the dashboard.',
            action: null,
          });
        }
        return of({
          replySpoken: 'Opening assessments.',
          action: navAction('/teacher/assessment', { schoolId: ctx.schoolId }),
        });
      },
    },
    {
      id: 'nav_class_selection',
      patterns: [
        /\b(open|go to)\s+class\s+selection\b/,
        /\bchoose\s+class\b/,
        /\bother\s+classrooms?\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening class selection.',
          action: navAction('/class-selection'),
        }),
    },
    {
      id: 'nav_progress_overview',
      patterns: [
        /\b(open|go to|show)\s+(the\s+)?(progress\s+overview|attendance)\b/,
        /\bprogress\s+overview\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening progress overview.',
          action: navAction('/progress-overview'),
        }),
    },
    {
      id: 'nav_bulk_assessment',
      patterns: [
        /\b(open|go to)\s+bulk\s+assessment\b/,
        /\bbulk\s+assess\b/,
      ],
      run: () =>
        of({
          replySpoken: 'Opening bulk assessment.',
          action: navAction('/bulk-assessment'),
        }),
    },
    {
      id: 'nav_go_back',
      patterns: [/\bgo\s+back\b/, /\bprevious\s+page\b/],
      run: () => {
        if (typeof window !== 'undefined') {
          window.history.back();
        }
        return of({
          replySpoken: 'Going back.',
          action: null,
        });
      },
    },
    {
      id: 'data_class_count',
      patterns: [
        /\bhow\s+many\s+classes\b/,
        /\bnumber\s+of\s+classes\b/,
        /\bcount\s+my\s+classes\b/,
      ],
      run: (ctx, deps) => {
        if (!ctx.teacherId) {
          return of({
            replySpoken: 'Please log in as a teacher to check your classes.',
            action: null,
          });
        }
        return deps.classRoom.getClassRoomByTeacherID(ctx.teacherId).pipe(
          map((res: any) => {
            const data = res?.data ?? res;
            const main = data?.mainClass;
            const hasMain =
              main &&
              typeof main === 'object' &&
              Object.keys(main).length > 0;
            const others: unknown[] = data?.otherClasses ?? [];
            const multi: unknown[] = data?.multiAssessmentClasses ?? [];
            const n =
              (hasMain ? 1 : 0) +
              (Array.isArray(others) ? others.length : 0) +
              (Array.isArray(multi) ? multi.length : 0);
            return {
              replySpoken: `You have ${n} class or program entries on your dashboard.`,
              action: null,
              metadata: { intent: 'data_class_count', count: n },
            } as LocalCommandResult;
          }),
          catchError(() =>
            of({
              replySpoken:
                'I could not load your class list. Open the dashboard to see details.',
              action: null,
            })
          )
        );
      },
    },
    {
      id: 'data_lesson_today',
      patterns: [
        /\b(today'?s|today)\s+lesson\s*plan\b/,
        /\bshow\s+(me\s+)?(the\s+)?(daily\s+)?plan\b/,
        /\bwhat\s+('?s|is)\s+(on|for)\s+today\b.*\bplan\b/,
      ],
      run: (ctx, deps) =>
        deps.store.select(selectSelectedMultiAssessment).pipe(
          take(1),
          switchMap((assessment: AssessmentConfig | null) => {
            const sectionId = Number(assessment?.section_id) || 0;
            const classLevelId = Number(assessment?.class_level_id) || 0;
            const hasPlan =
              assessment?.has_lesson_plan === true ||
              assessment?.has_lesson_plan === 1;
            if (!classLevelId || !ctx.schoolId || !hasPlan) {
              return of({
                replySpoken:
                  'No lesson plan is configured for this class.',
                action: null,
              });
            }
            const today = new Date();
            const y = today.getFullYear();
            const m = String(today.getMonth() + 1).padStart(2, '0');
            const d = String(today.getDate()).padStart(2, '0');
            const dateStr = `${y}-${m}-${d}`;
            const schoolId = String(ctx.schoolId);
            return deps.lessonPlan
              .getDailyLessonPlan(
                dateStr,
                schoolId,
                classLevelId,
                sectionId || undefined
              )
              .pipe(
              map((res: any) => {
                const plan = res?.data ?? res;
                const theme = plan?.theme_name || plan?.theme_number || 'today';
                return {
                  replySpoken: `Today's plan is themed ${theme}. Opening lesson plans.`,
                  action: navAction('/lesson-plan', {
                    classId: classLevelId,
                    sectionId,
                    classLevelId,
                  }),
                  metadata: { intent: 'data_lesson_today' },
                } as LocalCommandResult;
              }),
              catchError(() =>
                of({
                  replySpoken:
                    'I could not load today’s lesson plan. Open a program and try again.',
                  action: navAction('/lesson-plan', {
                    classId: classLevelId,
                    sectionId,
                    classLevelId,
                  }),
                })
              )
            );
          })
        ),
    },
    {
      id: 'data_parents_connected',
      patterns: [
        /\bhow\s+many\s+parents\b/,
        /\bparents?\s+connected\b/,
        /\bparent\s+count\b/,
      ],
      run: (ctx, deps) => {
        if (!ctx.classId) {
          return of({
            replySpoken: 'Select your class from the dashboard to count parents.',
            action: null,
          });
        }
        const ap =
          (typeof window !== 'undefined' &&
            Number(localStorage.getItem('assessmentPeriodId'))) ||
          0;
        return deps.parents.getParentsByClassId(ctx.classId, ap).pipe(
          map((res: any) => {
            const list = res?.data ?? res;
            const arr = Array.isArray(list) ? list : list?.parents ?? list?.rows;
            const n = Array.isArray(arr) ? arr.length : 0;
            return {
              replySpoken: `There are ${n} parent records for this class.`,
              action: null,
              metadata: { intent: 'data_parents_connected', count: n },
            } as LocalCommandResult;
          }),
          catchError(() =>
            of({
              replySpoken:
                'I could not load parent data. Check connect with parents from the menu.',
              action: null,
            })
          )
        );
      },
    },
  ];

  tryLocalCommand(
    rawText: string,
    role: UserRoleHint = 'teacher'
  ): Observable<LocalCommandResult | null> {
    const normalized = normalizeVoiceInput(rawText);
    if (!normalized) return of(null);

    const ctx: CommandContext = {
      normalizedText: normalized,
      role,
      ...this.readUserContext(),
    };

    const deps: RegistryDeps = {
      router: this.router,
      classRoom: this.classRoom,
      lessonPlan: this.lessonPlan,
      parents: this.parents,
      store: this.store,
    };

    for (const cmd of this.commands) {
      if (!ASSISTANT_NAVIGATION_ENABLED && isAssistantNavigationCommandId(cmd.id)) {
        continue;
      }
      if (cmd.roles?.length && !cmd.roles.includes(role)) continue;
      if (!cmd.patterns.some(p => p.test(normalized))) continue;
      return cmd.run(ctx, deps);
    }
    return of(null);
  }

  /**
   * Run a registered local command by id (same `run` as voice-matched commands).
   * Used for quick-action tiles without an AI round-trip.
   */
  runCommandById(
    id: string,
    role: UserRoleHint = 'teacher'
  ): Observable<LocalCommandResult | null> {
    const cmd = this.commands.find(c => c.id === id);
    if (!cmd) return of(null);
    if (!ASSISTANT_NAVIGATION_ENABLED && isAssistantNavigationCommandId(cmd.id)) {
      return of(null);
    }
    if (cmd.roles?.length && !cmd.roles.includes(role)) return of(null);

    const ctx: CommandContext = {
      normalizedText: '',
      role,
      ...this.readUserContext(),
    };

    const deps: RegistryDeps = {
      router: this.router,
      classRoom: this.classRoom,
      lessonPlan: this.lessonPlan,
      parents: this.parents,
      store: this.store,
    };

    return cmd.run(ctx, deps);
  }

  /** Short labels for quick-action chips in the UI (display via i18n; run by command id). */
  getQuickCommandPhrases(): { id: string; labelKey: string }[] {
    if (!ASSISTANT_NAVIGATION_ENABLED) return [];
    return [
      { id: 'nav_dashboard', labelKey: 'voiceAssistant.quickPhrases.openDashboard' },
      { id: 'nav_classroom', labelKey: 'voiceAssistant.quickPhrases.openClassroom' },
    ];
  }

  private readUserContext(): Omit<
    CommandContext,
    'normalizedText' | 'role'
  > {
    let teacherId: number | null = null;
    let schoolId: number | null = null;
    let classId: number | null = null;
    let teacherName: string | null = null;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        teacherId = Number(u.teacher_id) || null;
        schoolId = Number(u.school_id) || null;
        classId = Number(u.class_id) || null;
        teacherName = (u.teacher_name as string) || null;
      } catch {
        /* ignore */
      }
    }
    return {
      teacherId,
      schoolId,
      classId,
      teacherName,
      isFastrack: environment.envName === 'fastrack',
    };
  }
}

function navAction(
  path: string,
  params?: Record<string, string | number>
): AssistantAction {
  return {
    type: 'navigate',
    target: path,
    params: params ?? null,
  };
}
